import { Link, useNavigate } from "react-router-dom";
import {
  MdLockOutline,
  MdMailOutline,
  MdPermIdentity,
  MdCheckCircle,
  MdBusiness
} from "react-icons/md";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import {
  RegisterCredentials,
  register
} from "../../utils/api/endpoints/user/authentication";
import Form from "../forms/Form";
import AuthTextBox from "./inputs/AuthTextBox";
import Checkbox from "../forms/inputs/Checkbox";
import { UseFormSetError } from "react-hook-form";
import { EMAIL_REGEX } from "../..";
import { useMemo, useState } from "react";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import axios from "../../utils/api/axios";
import toast from "react-hot-toast";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../utils/auth/authConfig";
import CompanySearchDropdown from "./inputs/CompanySearchDropdown";
// Company logo UI disabled temporarily (frontend-only)
// import CompanyLogoUpload from "./inputs/CompanyLogoUpload";
// import { uploadCompanyLogo } from "../../utils/api/endpoints/companies";

type RegisterFields = {
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
  termsAndConditions: boolean;
  company_name?: string;
};

const RegisterForm = ({
  userType
}: {
  userType: UserRole.BUSINESS | UserRole.RESEARCHER;
}) => {
  const navigate = useNavigate();
  const [googleIdToken, setGoogleIdToken] = useState<string | undefined>();
  const [googleUserInfo, setGoogleUserInfo] = useState<
    { email: string; name: string } | undefined
  >();
  const [microsoftAccessToken, setMicrosoftAccessToken] = useState<
    string | undefined
  >();
  const [microsoftUserInfo, setMicrosoftUserInfo] = useState<
    { email: string; name: string } | undefined
  >();
  const { instance } = useMsal();
  const [companyName, setCompanyName] = useState<string>("");
  const [companyId, setCompanyId] = useState<number | undefined>();
  const [companyLogo, setCompanyLogo] = useState<File | null>(null);
  const [existingCompanyLogo, setExistingCompanyLogo] = useState<string | null>(null);

  const handleSubmit = async (
    data: RegisterFields,
    setError: UseFormSetError<RegisterFields>
  ) => {
    // Check if the terms and conditions are accepted
    if (!data.termsAndConditions) {
      setError("termsAndConditions", {
        message: "You must read and agree to the terms and conditions"
      });
      return; // Prevent further execution if terms are not accepted
    }

    // Check if company name is provided for business users
    if (userType === UserRole.BUSINESS && !companyName.trim()) {
      toast.error("Please enter your company/organization name");
      return;
    }

    // Logo requirement disabled (frontend-only change)
    // if (userType === UserRole.BUSINESS && !companyId && !existingCompanyLogo && !companyLogo) {
    //   toast.error("Please upload your company logo");
    //   return;
    // }

    // If Google SSO already done, finalize with username
    if (googleIdToken) {
      try {
        // check username availability
        const avail = await axios.get("/v2/username-available", {
          params: { username: data.username }
        });
        if (!avail.data?.available) {
          setError("username", { message: "Username already exists" });
          return;
        }

        await axios.post(
          "/v2/register/google",
          JSON.stringify({
            id_token: googleIdToken,
            username: data.username,
            role: userType,
            ...(userType === UserRole.BUSINESS && companyName
              ? { company_name: companyName }
              : {})
          }),
          { headers: { "Content-Type": "application/json" } }
        );

        toast.success("Registration successful! Admin approval pending.", {
          duration: 6000
        });
        navigate("/login");
        return;
      } catch (error: any) {
        console.error("Google registration error:", error);
        toast.error(error.response?.data?.message || "Registration failed");
        return;
      }
    }

    // If Microsoft SSO already done, finalize with username
    if (microsoftAccessToken) {
      try {
        // check username availability
        const avail = await axios.get("/v2/username-available", {
          params: { username: data.username }
        });
        if (!avail.data?.available) {
          setError("username", { message: "Username already exists" });
          return;
        }

        await axios.post(
          "/v2/register/microsoft",
          JSON.stringify({
            id_token: microsoftAccessToken,
            access_token: microsoftAccessToken,
            username: data.username,
            role: userType,
            ...(userType === UserRole.BUSINESS && companyName
              ? { company_name: companyName }
              : {})
          }),
          { headers: { "Content-Type": "application/json" } }
        );

        toast.success("Registration successful! Admin approval pending.", {
          duration: 6000
        });
        navigate("/login");
        return;
      } catch (error: any) {
        console.error("Microsoft registration error:", error);
        toast.error(error.response?.data?.message || "Registration failed");
        return;
      }
    }

    // Make sure the passwords match - triggering a form error if not
    if (data.password !== data.confirmPassword) {
      setError("confirmPassword", {
        message: "Passwords do not match"
      });
      return; // Prevent further execution if passwords do not match
    }

    // For business users with NEW company and logo, create company with logo first
    let finalCompanyId = companyId;

    console.log("DEBUG - Registration flow:", {
      userType,
      companyId,
      companyName,
      hasCompanyLogo: !!companyLogo,
      companyLogoName: companyLogo?.name,
      existingCompanyLogo
    });

    if (userType === UserRole.BUSINESS && !companyId && companyName && companyLogo) {
      try {
        console.log("Creating new company with logo before registration...");
        const { createCompany } = await import("../../utils/api/endpoints/companies");
        const newCompany = await createCompany(companyName, companyLogo);

        if (newCompany) {
          finalCompanyId = newCompany.company_id;
          console.log("Company created with logo, ID:", finalCompanyId);
          toast.success("Company created with logo successfully!");
        } else {
          toast.error("Failed to create company. Please try again.");
          return;
        }
      } catch (error) {
        console.error("Company creation failed:", error);
        toast.error("Failed to create company. Please try again.");
        return;
      }
    } else if (userType === UserRole.BUSINESS && !companyId && companyName) {
      console.log("⚠️ NEW COMPANY WITHOUT LOGO - This should not happen if logo is required!");
    }

    // Otherwise make the request to register the new user
    const registrationData = {
      role: userType,
      ...(data as RegisterCredentials),
      ...(userType === UserRole.BUSINESS && finalCompanyId
        ? { company_id: finalCompanyId }
        : userType === UserRole.BUSINESS && companyName
        ? { company_name: companyName }
        : {})
    };

    console.log("Registration data being sent:", registrationData);

    const registerResult = await register(registrationData);

    if (registerResult) {
      toast.success("Registration successful! Admin approval pending.", {
        duration: 6000
      });
      navigate("/login");
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      const idToken = credentialResponse.credential;
      setGoogleIdToken(idToken);

      // Decode the ID token to get user info (basic decode, not verification)
      const payload = JSON.parse(atob(idToken.split(".")[1]));
      const userInfo = {
        email: payload.email,
        name: payload.name || payload.email?.split("@")[0] || "User"
      };
      setGoogleUserInfo(userInfo);

      toast.success(`Signed in with Google as ${userInfo.name}`);
    } catch (error) {
      console.error("Google success error:", error);
      toast.error("Google sign-in failed");
    }
  };

  const handleGoogleError = () => {
    toast.error("Google sign-in failed");
  };

  const handleMicrosoftLogin = async () => {
    try {
      const response = await instance.loginPopup(loginRequest);
      if (response.idToken) {
        setMicrosoftAccessToken(response.idToken);

        // For now, we'll use the account info from the response
        const userInfo = {
          email: response.account?.username || "",
          name:
            response.account?.name ||
            response.account?.username?.split("@")[0] ||
            "User"
        };
        setMicrosoftUserInfo(userInfo);

        toast.success(`Signed in with Microsoft as ${userInfo.name}`);
      }
    } catch (error) {
      console.error("Microsoft login error:", error);
      toast.error("Microsoft sign-in failed");
    }
  };

  const defaultValues = useMemo(
    () => ({
      email: "",
      username: "",
      password: "",
      confirmPassword: "",
      termsAndConditions: false,
      company_name: ""
    }),
    []
  );

  return (
    <article className="mx-auto w-full max-w-3xl">
      {/* Header Section */}
      <div className="mb-8">
        <h1 className="mb-2 text-2xl font-semibold text-gray-900">Create your account</h1>
        <p className="text-sm text-gray-600">Fill in the details below to get started</p>
      </div>

        {/* SSO Buttons */}
        {!googleIdToken && !microsoftAccessToken && (
          <div className="mb-6 space-y-3">
            {/* Google SSO */}
            {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
              <button
                type="button"
                onClick={() => {
                  // Trigger Google login by clicking the hidden Google button
                  const googleBtn = document.querySelector('[aria-labelledby="button-label"]') as HTMLElement;
                  googleBtn?.click();
                }}
                className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                  <path fill="#34A853" d="M9.003 18c2.43 0 4.467-.806 5.956-2.184l-2.909-2.258c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9.001c0 1.452.348 2.827.957 4.041l3.007-2.332z"/>
                  <path fill="#EA4335" d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z"/>
                </svg>
                <span>Sign up with Google</span>
              </button>
            )}

            {/* Hidden Google Login for actual authentication */}
            {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
              <div className="hidden">
                <GoogleOAuthProvider
                  clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID as string}
                >
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                  />
                </GoogleOAuthProvider>
              </div>
            )}

            {/* Microsoft SSO */}
            {process.env.REACT_APP_MICROSOFT_CLIENT_ID && (
              <button
                type="button"
                onClick={handleMicrosoftLogin}
                className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path d="M9.5 1H1V9.5H9.5V1Z" fill="#F25022" />
                  <path d="M10.5 1H19V9.5H10.5V1Z" fill="#7FBA00" />
                  <path d="M9.5 10.5H1V19H9.5V10.5Z" fill="#00A4EF" />
                  <path d="M10.5 10.5H19V19H10.5V10.5Z" fill="#FFB900" />
                </svg>
                <span>Sign up with Microsoft</span>
              </button>
            )}

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-4 text-gray-500">Or continue with</span>
              </div>
            </div>
          </div>
        )}

        {/* SSO Success Badges - Clean design */}
        {(googleIdToken && googleUserInfo) ||
        (microsoftAccessToken && microsoftUserInfo) ? (
          <div className="mb-6 space-y-3">
            {googleIdToken && googleUserInfo && (
              <div className="flex items-center gap-3 rounded-md border border-green-200 bg-green-50 p-4">
                <MdCheckCircle className="text-xl text-green-600" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-green-900">
                    Connected with Google
                  </p>
                  <p className="text-xs text-green-700 truncate">
                    {googleUserInfo.email}
                  </p>
                </div>
              </div>
            )}
            {microsoftAccessToken && microsoftUserInfo && (
              <div className="flex items-center gap-3 rounded-md border border-blue-200 bg-blue-50 p-4">
                <MdCheckCircle className="text-xl text-blue-600" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-blue-900">
                    Connected with Microsoft
                  </p>
                  <p className="text-xs text-blue-700 truncate">
                    {microsoftUserInfo.email}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : null}

        <Form
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Two-column grid layout for better space utilization */}
          <div className="grid grid-cols-1 gap-y-6 md:grid-cols-2 md:gap-x-10">
            {/* Row 1: Email and Username */}
            {!googleIdToken && !microsoftAccessToken && (
              <AuthTextBox
                label="Email"
                name="email"
                placeholder="Enter your email address"
                rules={{
                  required: true,
                  pattern: {
                    value: EMAIL_REGEX,
                    message: "Please enter a valid email address."
                  }
                }}
                Icon={MdMailOutline}
                className="max-w-[520px]"
              />
            )}

            <AuthTextBox
              label="Username"
              name="username"
              placeholder="Enter your username"
              rules={{
                required: true
              }}
              Icon={MdPermIdentity}
              className={`${(googleIdToken || microsoftAccessToken) ? 'md:col-span-2 max-w-none' : 'max-w-[520px]'}`}
            />

            {/* Row 2: Password and Confirm Password */}
            {!googleIdToken && !microsoftAccessToken && (
              <>
                <AuthTextBox
                  label="Password"
                  name="password"
                  placeholder="Enter your password"
                  password={true}
                  rules={{
                    required: true,
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
                      message:
                        "Password should contain at least 8 characters, including at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)."
                    }
                  }}
                  Icon={MdLockOutline}
                  className="max-w-[520px]"
                />

                <AuthTextBox
                  label="Confirm Password"
                  name="confirmPassword"
                  placeholder="Confirm your password"
                  password={true}
                  rules={{
                    required: true
                  }}
                  Icon={MdLockOutline}
                  className="max-w-[520px]"
                />
              </>
            )}
          </div>

          {/* Company field for business users - Clean design */}
          {userType === UserRole.BUSINESS && (
            <>
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Company / Organization <span className="text-red-600">*</span>
                </label>
                <CompanySearchDropdown
                  value={companyName}
                  onChange={(name, id, logo) => {
                    setCompanyName(name);
                    setCompanyId(id);
                    setExistingCompanyLogo(logo || null);
          // Clear logo file if selecting existing company with logo
          if (logo) {
            setCompanyLogo(null);
          }
                  }}
                  placeholder="Search or add your company..."
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  Start typing to search or add a new company
                </p>
              </div>
              {/** Company Logo Upload UI disabled for now */}
              {/**
              <div className="md:col-span-2">
                <CompanyLogoUpload
                  value={companyLogo}
                  onChange={setCompanyLogo}
                  existingLogoUrl={existingCompanyLogo}
                  required={!companyId && !existingCompanyLogo}
                />
              </div>
              */}
            </>
          )}

          {/* Terms and Conditions - Clean design */}
          <div className="flex items-center gap-1.5 rounded-md bg-gray-50 px-3 py-3">
            <Checkbox name="termsAndConditions" />
            <label className="flex-1 text-sm leading-relaxed text-gray-600">
              By creating an account, you agree to our{" "}
                {userType === UserRole.RESEARCHER ? (
                  <>
                    <Link
                      to="https://capturethebug.xyz/Useful-Links/CTB-T&C"
                      target="_blank"
                      className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      Terms of Service
                    </Link>
                    ,{" "}
                    <Link
                      to="https://capturethebug.xyz/Useful-Links/Researcher-tc"
                      target="_blank"
                      className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      Researcher Terms
                    </Link>
                    ,{" "}
                    <Link
                      to="https://capturethebug.xyz/Useful-Links/Code-Of-Ethics"
                      target="_blank"
                      className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      Code of Ethics
                    </Link>
                    , and{" "}
                    <Link
                      to="https://capturethebug.xyz/Useful-Links/Disclosure-Policy"
                      target="_blank"
                      className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      Disclosure Policy
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="https://capturethebug.xyz/Useful-Links/CTB-T&C"
                      target="_blank"
                      className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="https://capturethebug.xyz/Useful-Links/Customer-Terms&Conditions"
                      target="_blank"
                      className="font-medium text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      Customer Terms
                    </Link>
                  </>
                )}
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full rounded-md bg-[#0B1731] px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-[#0e1d40] focus:outline-none focus:ring-2 focus:ring-[#0B1731] focus:ring-offset-2 flex items-center justify-center gap-3"
          >
            <span>
              {googleIdToken || microsoftAccessToken
                ? "Complete registration"
                : "Create Account"}
            </span>
            <img src="/icons/rightArrow.svg" alt="arrow" className="h-5 w-5" />
          </button>

        </Form>

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-sm md:text-base text-gray-600">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-blue-600 hover:text-blue-700">
            Sign in
          </Link>
        </p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-green-100 px-3 py-1.5 text-xs text-green-900">
          <img src="/icons/secureIcon.svg" alt="secure" className="h-4 w-4" />
          <span>Secure, encrypted connection</span>
        </div>
      </div>
    </article>
  );
};

export default RegisterForm;
